import { useState } from "react";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
} from "@mui/material";
import { formatMoney } from "./Utils";
import DownloadBreakoutButton from "./DownloadBreakoutButton";
import { calculateReplacementCost } from "api";

export default function Calculator(props) {
  const ep = props.estatedProperty;
  const defaultState = {
    is_condo: false,
    zip_code: ep && ep.address ? ep.address.zip_code : "",
    year_built: ep.property_info.year_built || null,
    building_shape_id: 1,
    total_area_sq_ft: ep.property_info.sqft || 0,
    has_attached_garage:
      (ep && ep.structures && ep.structures.hasAttachedGarage) || false,
    parking_spaces_count:
      ep && ep.structures ? ep.structures.parking_spaces_count || 0 : 0,
    fireplaces: ep && ep.structures ? ep.structures.fireplacesCount : 0,
    stories: ep && ep.structures ? ep.structures.storiesCount : 0,
    has_unfinished_basement:
      (ep && ep.structures && ep.structures.hasUnfinishedBasement) || false,
    has_finished_basement:
      (ep && ep.structures && ep.structures.hasFinishedBasement) || false,
    has_central_heating_cooling:
      (ep && ep.structures && ep.structures.hasCentralHeatingCooling) || false,
    has_central_heating:
      (ep && ep.structures && ep.structures.hasCentralHeating) || false,
    has_floor_or_wall_furnace:
      (ep && ep.structures && ep.structures.hasFloorOrWallFurnace) || false,
    balcony_sqft: ep ? ep.balconySqft : 0,
    wood_deck_sqft: ep ? ep.woodDeckSqft : 0,
    covered_porch_sqft: ep ? ep.coveredPorchSqft : 0,
  };
  const [is_condo, setIsCondo] = useState(defaultState.is_condo);
  const [year_built, setYearBuilt] = useState(defaultState.year_built);
  const [building_shape_id, setBuildingShapeId] = useState(
    defaultState.building_shape_id
  );
  const [total_area_sq_ft, setTotalAreaSqFt] = useState(
    defaultState.total_area_sq_ft
  );
  const [has_attached_garage, setHasAttachedGarage] = useState(
    defaultState.has_attached_garage
  );
  const [parking_spaces_count, setParkingSpacesCount] = useState(
    defaultState.parking_spaces_count
  );
  const [fireplaces, setFireplaces] = useState(defaultState.fireplaces);
  const [stories, setStories] = useState(defaultState.stories);
  const [has_unfinished_basement, setHasUnfinishedBasement] = useState(
    defaultState.has_unfinished_basement
  );
  const [has_finished_basement, setHasFinishedBasement] = useState(
    defaultState.has_finished_basement
  );
  const [has_central_heating_cooling, setHasCentralHeatingCooling] = useState(
    defaultState.has_central_heating_cooling
  );
  const [has_central_heating, setHasCentralHeating] = useState(
    defaultState.has_central_heating
  );
  const [has_floor_or_wall_furnace, setHasFloorOrWallFurnace] = useState(
    defaultState.has_floor_or_wall_furnace
  );
  const [balcony_sqft, setBalconySqft] = useState(defaultState.balcony_sqft);
  const [wood_deck_sqft, setWoodDeckSqft] = useState(
    defaultState.wood_deck_sqft
  );
  const [covered_porch_sqft, setCoveredPorchSqft] = useState(
    defaultState.covered_porch_sqft
  );
  const [selected_custom_user_input_id, setSelectedCustomUserInputId] =
    useState(0); // default to no user input

  const computeUserInputKey = (val) =>
    JSON.stringify(
      Object.keys(defaultState).reduce(
        (o, key) => ({ ...o, [key]: val[key] }),
        {}
      )
    );
  const getUniqueCustomUserInputs = () => {
    const uniqueCustomUserInputKeys = new Set();
    uniqueCustomUserInputKeys.add(computeUserInputKey(defaultState));
    const uniqueCustomUserInputs = [];
    ep.custom_replacement_cost_inputs.forEach((val) => {
      const key = computeUserInputKey(val);
      if (!uniqueCustomUserInputKeys.has(key)) {
        uniqueCustomUserInputKeys.add(key);
        uniqueCustomUserInputs.push(val);
      }
    });
    uniqueCustomUserInputs.sort((a, b) =>
      new Date(a.created_at) > new Date(b.created_at) ? -1 : 1
    );
    return uniqueCustomUserInputs;
  };
  const [uniqueCustomUserInputs, setUniqueCustomUserInputs] = useState(
    getUniqueCustomUserInputs()
  );

  const [data, setData] = useState({
    Luxury: null,
    "Semi Luxury": null,
    "Best Standard": null,
    "Good Standard": null,
    "Avg Standard": null,
    "Min Standard": null,
  });

  const basePayload = {
    is_condo,
    zip_code: defaultState.zip_code,
    year_built,
    building_shape_id,
    total_area_sq_ft,
    has_attached_garage,
    parking_spaces_count,
    fireplaces,
    stories,
    has_unfinished_basement,
    has_finished_basement,
    has_central_heating_cooling,
    has_central_heating,
    has_floor_or_wall_furnace,
    balcony_sqft,
    wood_deck_sqft,
    covered_porch_sqft,
  };
  const onCalculateClick = () => {
    const payload = {
      ...basePayload,
      email: props.profile.email,
      first_name: props.profile.given_name,
      last_name: props.profile.family_name,
      fips: ep && ep.address ? ep.address.fips : null,
      apn: ep && ep.address ? ep.address.apn : null,
      formatted_street_address:
        ep && ep.address ? ep.address.formatted_street_address : null,
      city: ep && ep.address ? ep.address.city : null,
      state: ep && ep.address ? ep.address.state : null,
    };
    ep.appendCustomReplacementCostInput({
      id: Math.floor(Number.MAX_SAFE_INTEGER * Math.random()),
      ...payload,
      created_at: new Date().toISOString().slice(0, -1),
    });
    calculateReplacementCost(payload).then((d) => {
      setData({
        Luxury: formatMoney(Math.round(d.luxury)),
        "Semi Luxury": formatMoney(Math.round(d.semi_luxury)),
        "Best Standard": formatMoney(Math.round(d.best_std)),
        "Good Standard": formatMoney(Math.round(d.good_std)),
        "Avg Standard": formatMoney(Math.round(d.avg_std)),
        "Min Standard": formatMoney(Math.round(d.min_std)),
      });
      const newUserInputs = getUniqueCustomUserInputs();
      setUniqueCustomUserInputs(newUserInputs);
      const key = computeUserInputKey(payload);
      const currentState = newUserInputs.find(
        (_) => key === computeUserInputKey(_)
      );
      if (currentState) {
        setSelectedCustomUserInputId(currentState.id);
      } else {
        setSelectedCustomUserInputId(0);
      }
    });
  };

  const updateCustomInputs = (id) => {
    setSelectedCustomUserInputId(id);
    const selectedInput =
      uniqueCustomUserInputs.find((_) => _.id === id) || defaultState;
    setIsCondo(selectedInput.is_condo);
    setYearBuilt(selectedInput.year_built);
    setBuildingShapeId(selectedInput.building_shape_id);
    setTotalAreaSqFt(selectedInput.total_area_sq_ft);
    setHasAttachedGarage(selectedInput.has_attached_garage);
    setParkingSpacesCount(selectedInput.parking_spaces_count);
    setFireplaces(selectedInput.fireplaces);
    setStories(selectedInput.stories);
    setHasUnfinishedBasement(selectedInput.has_unfinished_basement);
    setHasFinishedBasement(selectedInput.has_finished_basement);
    setHasCentralHeatingCooling(selectedInput.has_central_heating_cooling);
    setHasCentralHeating(selectedInput.has_central_heating);
    setHasFloorOrWallFurnace(selectedInput.has_floor_or_wall_furnace);
    setBalconySqft(selectedInput.balcony_sqft);
    setWoodDeckSqft(selectedInput.wood_deck_sqft);
    setCoveredPorchSqft(selectedInput.covered_porch_sqft);
  };

  const getCurrentDefaultValue = (key) => {
    return (uniqueCustomUserInputs.find(
      (_) => _.id === selected_custom_user_input_id
    ) || defaultState)[key];
  };

  const customUserInputsSelect = (
    <Select
      value={selected_custom_user_input_id}
      onChange={(event) => updateCustomInputs(event.target.value)}
      label="Custom User Inputs"
    >
      <MenuItem value={0}>Default</MenuItem>
      {uniqueCustomUserInputs.map((obj, index) => {
        let date = new Date(obj.created_at);
        date = new Date(
          Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds()
          )
        );
        return (
          <MenuItem key={index} value={obj.id}>
            {obj.first_name} {obj.last_name} - ({date.toLocaleDateString()},{" "}
            {date.toLocaleTimeString()})
          </MenuItem>
        );
      })}
    </Select>
  );
  const downloadBreakoutButton = data.Luxury ? (
    <DownloadBreakoutButton
      style={{ float: "right", marginTop: 16, position: "sticky", top: 294 }}
      body={basePayload}
      {...props}
    />
  ) : null;

  return (
    <Grid container spacing={2}>
      <Grid item sm={12}>
        <FormControl sx={{ width: "100%" }}>
          <InputLabel>Custom User Inputs</InputLabel>
          {customUserInputsSelect}
        </FormControl>
      </Grid>
      <Grid item sm={12} md={6}>
        <Grid container direction={"column"} spacing={2}>
          <Grid item>
            <FormControlLabel
              label="Is Condo"
              control={
                <Checkbox
                  checked={is_condo}
                  onChange={(e) => setIsCondo(e.target.checked)}
                />
              }
            />
          </Grid>
          <Grid item>
            <TextField
              label="Zip Code"
              disabled
              value={defaultState.zip_code}
            />
          </Grid>
          <Grid item>
            <TextField
              label="Year Built"
              value={year_built}
              onChange={(e) =>
                setYearBuilt(parseInt(e.target.value, 10) || undefined)
              }
              onBlur={() =>
                year_built <= 1600 || !year_built
                  ? setYearBuilt(getCurrentDefaultValue("year_built"))
                  : null
              }
              type="number"
            />
          </Grid>
          <Grid item>
            <FormControl sx={{ width: 195 }}>
              <InputLabel># of Roof Corners</InputLabel>
              <Select
                value={building_shape_id}
                onChange={(event) => {
                  setBuildingShapeId(event.target.value);
                }}
                label="# of Roof Corners"
              >
                <MenuItem value={1}>4</MenuItem>
                <MenuItem value={2}>6</MenuItem>
                <MenuItem value={3}>8</MenuItem>
                <MenuItem value={4}>10</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <TextField
              label="Total Area Sq Ft"
              value={total_area_sq_ft}
              onChange={(e) =>
                setTotalAreaSqFt(parseInt(e.target.value, 10) || undefined)
              }
              onBlur={() =>
                total_area_sq_ft <= 0 || !total_area_sq_ft
                  ? setTotalAreaSqFt(getCurrentDefaultValue("total_area_sq_ft"))
                  : null
              }
              type="number"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              label="Has Attached Garage"
              control={
                <Checkbox
                  checked={has_attached_garage}
                  onChange={(e) => setHasAttachedGarage(e.target.checked)}
                />
              }
            />
          </Grid>
          <Grid item>
            <TextField
              label="Parking Spaces Count"
              value={parking_spaces_count}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setParkingSpacesCount(val === 0 ? 0 : val || undefined);
              }}
              onBlur={() =>
                parking_spaces_count < 0 || parking_spaces_count === undefined
                  ? setParkingSpacesCount(
                      getCurrentDefaultValue("parking_spaces_count")
                    )
                  : null
              }
              type="number"
            />
          </Grid>
          <Grid item>
            <TextField
              label="Fireplaces Count"
              value={fireplaces}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setFireplaces(val === 0 ? 0 : val || undefined);
              }}
              onBlur={() =>
                fireplaces < 0 || fireplaces === undefined
                  ? setFireplaces(getCurrentDefaultValue("fireplaces"))
                  : null
              }
              type="number"
            />
          </Grid>
          <Grid item>
            <TextField
              label="Stories Count"
              value={stories}
              onChange={(e) =>
                setStories(parseFloat(e.target.value, 10) || undefined)
              }
              onBlur={() =>
                stories <= 0 || !stories
                  ? setStories(getCurrentDefaultValue("stories"))
                  : null
              }
              type="number"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              label="Has Unfinished Basement"
              control={
                <Checkbox
                  checked={has_unfinished_basement}
                  onChange={(e) => setHasUnfinishedBasement(e.target.checked)}
                />
              }
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              label="Has Finished Basement"
              control={
                <Checkbox
                  checked={has_finished_basement}
                  onChange={(e) => setHasFinishedBasement(e.target.checked)}
                />
              }
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              label="Has Central Heating and Cooling"
              control={
                <Checkbox
                  checked={has_central_heating_cooling}
                  onChange={(e) =>
                    setHasCentralHeatingCooling(e.target.checked)
                  }
                />
              }
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              label="Has Central Heating"
              control={
                <Checkbox
                  checked={has_central_heating}
                  onChange={(e) => setHasCentralHeating(e.target.checked)}
                />
              }
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              label="Has Floor or Wall Furnace"
              control={
                <Checkbox
                  checked={has_floor_or_wall_furnace}
                  onChange={(e) => setHasFloorOrWallFurnace(e.target.checked)}
                />
              }
            />
          </Grid>
          <Grid item>
            <TextField
              label="Balcony Sq Ft"
              value={balcony_sqft}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setBalconySqft(val === 0 ? 0 : val || undefined);
              }}
              onBlur={() =>
                balcony_sqft < 0 || balcony_sqft === undefined
                  ? setBalconySqft(getCurrentDefaultValue("balcony_sqft"))
                  : null
              }
              type="number"
            />
          </Grid>
          <Grid item>
            <TextField
              label="Wood Deck Sq Ft"
              value={wood_deck_sqft}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setWoodDeckSqft(val === 0 ? 0 : val || undefined);
              }}
              onBlur={() =>
                wood_deck_sqft < 0 || wood_deck_sqft === undefined
                  ? setWoodDeckSqft(getCurrentDefaultValue("wood_deck_sqft"))
                  : null
              }
              type="number"
            />
          </Grid>
          <Grid item>
            <TextField
              label="Covered Porch Sq Ft"
              value={covered_porch_sqft}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setCoveredPorchSqft(val === 0 ? 0 : val || undefined);
              }}
              onBlur={() =>
                covered_porch_sqft < 0 || covered_porch_sqft === undefined
                  ? setCoveredPorchSqft(
                      getCurrentDefaultValue("covered_porch_sqft")
                    )
                  : null
              }
              type="number"
            />
          </Grid>
          <Grid item>
            <Button variant="outlined" onClick={onCalculateClick}>
              Calculate
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item sm={12} md={6}>
        <Table size="small" style={{ position: "sticky", top: 80 }}>
          <TableBody>
            {Object.keys(data).map((header, index) => (
              <TableRow key={index}>
                <TableCell>{header}</TableCell>
                <TableCell align="right">{data[header]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {downloadBreakoutButton}
      </Grid>
    </Grid>
  );
}
